import { inject, Injectable, Injector, signal } from '@angular/core';

import { AuthService } from '../../../../../services/auth.service';
import { DdrSpendService, ExtraUsageWindow } from '../../../../../services/ddr-spend.service';
import { extractErrorDetails } from '../../../../../utils/errorUtils';

/** What the plan-cap banner offers (CIQ-2230). `unavailable` falls back to the old CTA. */
export type ExtraUsageCtaState = 'unknown' | 'unavailable' | 'has_extra_usage' | 'turn_on' | 'request' | 'requested';

/** One read per user, workspace and cap window, shared by the banner's link and button variants. */
@Injectable({ providedIn: 'root' })
export class ExtraUsageCtaStore {
    private readonly injector = inject(Injector);

    readonly state = signal<ExtraUsageCtaState>('unknown');
    readonly sending = signal(false);
    private loadedKey: string | null = null;
    private loadingKey: string | null = null;
    private loadSeq = 0;

    async load(window: ExtraUsageWindow): Promise<void> {
        const key = this.keyFor(window);
        if (this.loadedKey === key || this.loadingKey === key) {
            return;
        }
        const seq = ++this.loadSeq;
        this.loadedKey = null;
        this.loadingKey = key;
        this.state.set('unknown');
        try {
            const own = await this.injector.get(DdrSpendService).getOwnExtraUsage(window);
            if (seq !== this.loadSeq) {
                return;
            }
            this.loadedKey = key;
            if (own.available === false) {
                this.state.set('unavailable');
            } else if (own.extra_usage) {
                this.state.set('has_extra_usage');
            } else if (own.can_turn_on) {
                this.state.set('turn_on');
            } else {
                this.state.set(own.request_status ? 'requested' : 'request');
            }
        } catch (err) {
            if (seq !== this.loadSeq) {
                return;
            }
            if ((err as { status?: number })?.status === 404) {
                this.loadedKey = key;
            }
            this.state.set('unavailable');
        } finally {
            if (seq === this.loadSeq) {
                this.loadingKey = null;
            }
        }
    }

    /** Forgets the last read and drops one in flight, so the next plan-cap block reads again. */
    reset(): void {
        this.loadSeq++;
        this.loadedKey = null;
        this.loadingKey = null;
    }

    /** Sends the member's request. Returns the reason on failure, or null. */
    async request(meter: string, window: ExtraUsageWindow): Promise<string | null> {
        if (this.sending() || this.state() !== 'request') {
            return null;
        }
        const key = this.loadedKey ?? this.keyFor(window);
        this.sending.set(true);
        try {
            await this.injector.get(DdrSpendService).requestExtraUsage(meter, window);
            if (this.loadingKey === key) {
                this.loadSeq++;
                this.loadingKey = null;
                this.loadedKey = key;
            }
            if (this.loadedKey === key) {
                this.state.set('requested');
            }
            return null;
        } catch (err) {
            console.error('Failed to request extra usage', err);
            return extractErrorDetails(err, 'Could not send the request.');
        } finally {
            this.sending.set(false);
        }
    }

    private keyFor(window: ExtraUsageWindow): string {
        const user = this.injector.get(AuthService).state().user;
        const period = new Date().toISOString().slice(0, window === 'daily' ? 10 : 7);
        return `${user?.user_id}:${user?.workspace_id}:${window}:${period}`;
    }
}
